// Importar clientes y su historial de compras desde Excel o CSV (traído del prototipo, ImportarClientes, y ampliado
// para los Excel de ventas por año: una fila por compra, en una o varias hojas). Funciones puras, con pruebas.
import { PAIS, PAISES, buscarPais, localDe, validarNumero, type Pais } from './paises';
import { emailOk, modoOracion, nombrePropio, parseDec, sinTildes } from './texto';
import type { CeldaLeida, HojaLeida } from './xlsx';

export type Campo = 'nombre' | 'tel' | 'pais' | 'email' | 'notas' | 'fecha' | 'producto' | 'cantidad' | 'valor';
export const CAMPOS: { id: Campo; label: string; requerido?: boolean }[] = [
  { id: 'nombre', label: 'Nombre', requerido: true }, { id: 'tel', label: 'WhatsApp', requerido: true }, { id: 'pais', label: 'País' }, { id: 'email', label: 'Correo' },
  { id: 'notas', label: 'Notas' }, { id: 'fecha', label: 'Fecha de compra' }, { id: 'producto', label: 'Producto' }, { id: 'cantidad', label: 'Cantidad' }, { id: 'valor', label: 'Valor (COP)' },
];
const ALIAS: Record<Campo, string[]> = {
  nombre: ['nombre', 'name', 'cliente', 'nombre completo', 'nombres', 'nombre cliente', 'nombre del cliente', 'comprador', 'clienta'],
  tel: ['whatsapp', 'telefono', 'celular', 'movil', 'phone', 'numero', 'tel', 'whatsapp telefono', 'numero de whatsapp', 'cel', 'contacto', 'telefono celular'],
  pais: ['pais', 'country'],
  email: ['correo', 'email', 'e mail', 'mail', 'correo electronico'],
  notas: ['notas', 'nota', 'observaciones', 'observacion', 'comentarios', 'comentario'],
  fecha: ['fecha', 'fecha compra', 'fecha de compra', 'fecha pedido', 'fecha del pedido', 'fecha venta', 'fecha de venta', 'date'],
  producto: ['producto', 'prenda', 'referencia', 'articulo', 'descripcion', 'item', 'productos'],
  cantidad: ['cantidad', 'cant', 'unidades', 'und', 'qty'],
  valor: ['valor', 'total', 'precio', 'venta', 'monto', 'valor total', 'precio total', 'valor venta', 'total venta'],
};
export const normCol = (h: unknown) => sinTildes(String(h ?? '')).replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
export type Columnas = Partial<Record<Campo, number>>;

// Columnas por su encabezado: primero el nombre exacto, después el que empieza igual ("Celular cliente").
export function detectarColumnas(encabezados: CeldaLeida[]): Columnas {
  const hs = encabezados.map(normCol), out: Columnas = {}, usadas = new Set<number>();
  for (const k of Object.keys(ALIAS) as Campo[]) {
    let i = hs.findIndex((h, j) => !usadas.has(j) && ALIAS[k].includes(h));
    if (i < 0) i = hs.findIndex((h, j) => !usadas.has(j) && !!h && ALIAS[k].some(a => h.startsWith(a + ' ')));
    if (i >= 0) { out[k] = i; usadas.add(i); }
  }
  return out;
}
// La fila de encabezados puede no ser la primera (un título arriba): se busca entre las 10 primeras la que nombra
// al cliente y su número.
export function filaEncabezado(h: HojaLeida): number {
  for (let i = 0; i < Math.min(10, h.filas.length); i++) { const c = detectarColumnas(h.filas[i] || []); if (c.nombre != null && c.tel != null) return i; }
  return -1;
}

const esRD = (local: string) => ['809', '829', '849'].includes(local.slice(0, 3));
// WhatsApp con su país: "+1 305…", "573005550101", "(300) 555-0103" con país por defecto, o el país de su columna.
export function numeroDesde(raw: CeldaLeida, paisTxt: CeldaLeida, paisDef: string): { pais: string; whatsapp: string } | { error: string } {
  const txt = typeof raw === 'number' ? String(Math.round(raw)) : String(raw ?? '').trim();
  let dig = txt.replace(/\D/g, '');
  if (!dig) return { error: 'Sin número' };
  const pt = String(paisTxt ?? '').trim();
  let pais: Pais | null = pt ? PAIS[pt.toUpperCase()] || buscarPais(pt) : null;
  if (!pais && (txt.startsWith('+') || txt.startsWith('00') || dig.length > 10)) {
    if (txt.startsWith('00')) dig = dig.slice(2);
    for (const p of PAISES.filter(p => dig.startsWith(p.dial)).sort((a, b) => b.dial.length - a.dial.length)) {
      const local = dig.slice(p.dial.length), pp = p.dial === '1' ? (esRD(local) ? PAIS.DO : PAIS.US) : p;
      if (!validarNumero(pp, local)) return { pais: pp.iso, whatsapp: pp.dial + local };
    }
  }
  pais = pais || PAIS[paisDef] || PAIS.CO;
  const local = localDe(pais, txt), pp = pais.dial === '1' && !pt && esRD(local) ? PAIS.DO : pais;
  const err = validarNumero(pp, local);
  return err ? { error: err } : { pais: pp.iso, whatsapp: pp.dial + local };
}

// Fechas: ISO (las celdas con formato de fecha ya llegan así), "15/03/2023", "15-03-23" o número de serie de Excel.
export function fechaDesde(v: CeldaLeida): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return v > 20000 && v < 80000 ? new Date(Math.round((v - 25569) * 864e5)).toISOString().slice(0, 10) : '';
  const s = v.trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return valida(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m) return valida(m[3].length === 2 ? 2000 + +m[3] : +m[3], +m[2], +m[1]);
  return '';
}
function valida(y: number, mes: number, d: number): string {
  const f = new Date(Date.UTC(y, mes - 1, d));
  return f.getUTCFullYear() === y && f.getUTCMonth() === mes - 1 && f.getUTCDate() === d && y >= 2000 && y <= 2100 ? f.toISOString().slice(0, 10) : '';
}
// Valores: número de la celda, o texto como "$ 175.000" o "175000,50".
export function valorDesde(v: CeldaLeida): number {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/[$\s]|COP|USD|DOP|RD/gi, '');
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) return +s.replace(/\./g, '').replace(',', '.'); // 175.000 o 1.250.000,50
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) return +s.replace(/,/g, '');                     // 175,000 o 1,250,000.50
  return s ? parseDec(s) ?? 0 : 0;
}

export interface CompraImportada { fecha: string; producto: string; cantidad: number; valor: number }
export interface ClienteImportado { whatsapp: string; pais: string; nombre: string; email: string; notas: string; compras: CompraImportada[]; filas: string[] }
export interface ErrorImportacion { donde: string; nombre: string; motivo: string }
export interface Analisis { clientes: ClienteImportado[]; errores: ErrorImportacion[]; filas: number; sinFecha: number }
export interface HojaElegida { hoja: HojaLeida; encabezado: number; columnas: Columnas }

// Agrupa las filas por WhatsApp: un cliente con todas sus compras, de todas las hojas y archivos.
// Una fila sin fecha no suma compra (solo datos del cliente).
export function analizar(hojas: HojaElegida[], paisDef: string): Analisis {
  const por = new Map<string, ClienteImportado>(), errores: ErrorImportacion[] = [];
  let filas = 0, sinFecha = 0;
  for (const { hoja, encabezado, columnas: c } of hojas) {
    for (let i = encabezado + 1; i < hoja.filas.length; i++) {
      const r = hoja.filas[i] || [], val = (k: Campo) => (c[k] != null ? r[c[k]!] ?? null : null);
      if (r.every(x => x == null || String(x).trim() === '')) continue;
      filas++;
      const donde = `${hoja.nombre}, fila ${i + 1}`, nombre = nombrePropio(String(val('nombre') ?? '').trim());
      if (!nombre) { errores.push({ donde, nombre: '', motivo: 'Sin nombre' }); continue; }
      const n = numeroDesde(val('tel'), val('pais'), paisDef);
      if ('error' in n) { errores.push({ donde, nombre, motivo: n.error }); continue; }
      let k = por.get(n.whatsapp);
      if (!k) por.set(n.whatsapp, k = { ...n, nombre, email: '', notas: '', compras: [], filas: [] });
      k.filas.push(donde);
      const email = String(val('email') ?? '').trim().toLowerCase();
      if (!k.email && emailOk(email)) k.email = email;
      const notas = modoOracion(String(val('notas') ?? ''));
      if (notas && !k.notas.includes(notas)) k.notas = [k.notas, notas].filter(Boolean).join(' ');
      if (c.fecha == null) continue;
      const fecha = fechaDesde(val('fecha'));
      if (!fecha) { sinFecha++; continue; }
      const cant = valorDesde(val('cantidad'));
      k.compras.push({ fecha, producto: String(val('producto') ?? '').replace(/\s+/g, ' ').trim(), cantidad: cant > 0 ? cant : 1, valor: Math.max(0, valorDesde(val('valor'))) });
    }
  }
  return { clientes: [...por.values()], errores, filas, sinFecha };
}
